/**
 * Panel copy for Details. Engine labels stay technical; this is for people.
 * The raw evidence ledger repeats the observation log — panelEvidence strips that.
 */

import type { EvidenceLine } from "./types";

const PLATFORM: Record<string, string> = {
  VIIRS_NOAA21: "NOAA-21",
  VIIRS_NOAA20: "NOAA-20",
  VIIRS_SNPP: "Suomi NPP",
  MODIS: "MODIS",
  MTG: "Meteosat (MTG)",
  MSG: "Meteosat (MSG)",
  S3: "Sentinel-3",
};

function platformFromKey(key: string): string {
  const raw = key.split(":")[1] ?? "";
  return PLATFORM[raw] ?? raw.replace(/_/g, " ");
}

/**
 * Drop the log-odds ledger items people already see as satellite looks.
 * One pass gets an explicit “only one overpass” line instead of restating the pixels.
 */
export function panelEvidence(
  lines: EvidenceLine[] | null | undefined,
  opts: { passCount: number; platformCount: number; canPromoteToObserved?: boolean },
): EvidenceLine[] {
  // Keep Sentinel-3 thermal lines — they are not in the MW log the same way.
  const out = (lines ?? []).filter(
    (e) => e.key !== "prior" && !(e.key.startsWith("thermal:") && !e.key.startsWith("thermal:S3:")),
  );
  // Pinpricks cannot become Observed from another trace — do not promise that.
  if (opts.canPromoteToObserved === false) return out;
  if (opts.passCount === 1) {
    out.unshift({
      key: "one_pass",
      label: "Only one satellite overpass",
      polarity: "prior",
      strength: "weak",
      delta_logit: 0,
    });
  } else if (out.length === 0 && opts.passCount > 1 && opts.platformCount === 1) {
    out.unshift({
      key: "same_sat",
      label: "Same satellite, more than one look",
      polarity: "prior",
      strength: "weak",
      delta_logit: 0,
    });
  }
  return out;
}

/** One sentence per evidence term. Keep numbers; drop jargon. */
export function humanEvidenceLine(e: EvidenceLine): string {
  if (e.key === "prior") {
    return "We start skeptical. Weather is not a fire until a satellite sees heat.";
  }
  if (e.key === "one_pass") {
    return "Only one satellite has looked so far. Polar satellites typically come back about every 12 hours. A second satellite would make this Observed.";
  }
  if (e.key === "same_sat") {
    return "The same satellite came back and still saw heat. A different satellite would make this Observed.";
  }
  if (e.key === "stale") {
    const hours = e.label.match(/(\d+)\s*h/);
    return hours
      ? `Nothing else has confirmed this in ${hours[1]} hours, so we stay cautious.`
      : "Nothing else has confirmed this, so we stay cautious.";
  }
  if (e.key.startsWith("thermal:")) {
    const who = platformFromKey(e.key);
    const n = e.label.match(/(\d+) px/);
    const frp = e.label.match(/FRP ([\d.]+) MW/);
    const px = n?.[1] ?? "1";
    const mw = frp?.[1] ?? "?";
    const tiny = e.label.includes(" trace ");
    // EFFIS Sentinel-3 rows have no megawatts — do not print 0.0 MW.
    if (who === "Sentinel-3" && (mw === "?" || mw === "0" || mw === "0.0")) {
      const plural = px === "1" ? "pixel" : "pixels";
      return `Sentinel-3 saw heat in ${px} ${plural} on one pass.`;
    }
    if (tiny) return `${who} picked up a tiny heat pixel (${mw} MW). That is not enough on its own.`;
    const plural = px === "1" ? "pixel" : "pixels";
    return `${who} saw heat in ${px} ${plural} (${mw} MW) on one pass.`;
  }
  if (e.key === "persist") return "The same satellite saw the heat again a few minutes later — it is still there.";
  if (e.key === "independent") return "Two different satellites saw heat here. That is why this is Observed.";
  if (e.key === "sunglint") return "This looks like sunglint or hot ground, not a new fire.";
  if (e.key === "frp_growth") return "A later pass measured a hotter fire than the first look.";
  if (e.key === "spatial") return "A later pass saw the heat covering more ground.";
  if (e.key === "fwi") return "The fire-weather index is extreme here.";
  if (e.key === "dry") return "Fine fuel is very dry.";
  if (e.key === "wind") return "Strong wind or gusts.";
  if (e.key === "human") return e.label.replace("People within", "People live within");
  if (e.key === "water") return "This sits on water — usually not a wildfire.";
  if (e.key === "industrial") return "This is an industrial site — often a furnace, not a wildfire.";
  if (e.key === "hot") return e.label.replace("Static hot target within", "A known hot stack or flare is within");
  if (e.key === "static_recur") {
    return "This heat has come back for days without growing like a wildfire — usually a furnace or flare.";
  }
  if (e.key === "hour_vpd") return "This hour is among the driest of the last day or two.";
  if (e.key === "hour_rh") return "Humidity is among the lowest hours.";
  if (e.key === "hour_gust") return "Gusts are ramping up versus this morning.";
  if (e.key === "hour_afternoon") return "It is the local afternoon on a peak-dry hour.";
  if (e.key === "fuel_fine") return "Fine fuel (grass, shrub, or crop) can ignite fast.";
  return e.label;
}
